import React from "react";
import groupBy from "lodash/groupBy";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import transform from "lodash/transform";
import _isArray from "lodash/isArray";
import { Detail } from "react-calendar";
import { compose } from "redux";
import {
  CalendarDate,
  CheckboxGroup,
  DatePickerType,
  DatePickerUniversal,
  DateValidity,
  DropdownCheckbox,
  ErrorMessageLocation,
  FilterControl,
  FilterDrawer,
  ICheckboxGroupValue,
  IDropdownCheckboxProps,
  IFilterTagProps,
  IOption,
  IRangeSelectionValue,
  LocationsSearch,
  RangeSelection,
} from "component-library";
import {
  FEATURE_FLAGS,
  withLaunchDarklyConsumerHOC,
  LaunchDarklyClientWithFlags,
  sendJobsfeedUsageHeapEventWithUserPrefix,
  JobsFeedUsageEvents,
} from "shared-services";
import { withTranslation, WithTranslation } from "i18n";
import { findFacets } from "shared/helpers/facetHelpers";
import { mapCheckboxValueToList } from "shared/helpers/mappers";
import { IFacetGroup } from "shared/models/facet";
import { ValueOf } from "shared/types/utilities";

import {
  mapCheckboxGroupValueToNumericValues,
  mapNumericOptionsToCheckboxGroupValue,
  mapOptionsToCheckboxGroupValue,
} from "helpers/mappers";

import { ChangeFilters } from "hoc/withJobsFilter/withJobsFilter";
import {
  FilterType,
  IFilterConfig,
  IFilterModel,
  IJobSender,
} from "hoc/withJobsFilter/withJobsFilter.config";

import {
  getTagId,
  getTagName,
} from "components/JobsFilterControl/JobsFilterControl.helpers";
import _uniqBy from "lodash/uniqBy";
import { getPlayingAgeLabel } from "shared-utils";
import { PlayingAge } from "shared-types";
import _isNumber from "lodash/isNumber";
import getPlayingAgePillTitle from "component-library/src/components/Organisms/Filters/FilterFileds/PlayingAgeFilter/helpers/getPlayingAgePillTitle";

export enum FilterControlType {
  Dropdown = "dropdown",
  DatePicker = "datePicker",
  CheckboxGroup = "checkboxGroup",
  Location = "Locations",
}

export type IJobsFilterControlModel = Pick<
  IFilterModel,
  | FilterType.ProductionType
  | FilterType.ContractType
  | FilterType.DateSent
  | FilterType.PlayingAgeMin
  | FilterType.PlayingAgeMax
  | FilterType.Locations
>;

export interface IJobsFilterTag {
  tagProps: IFilterTagProps;
  filterType: FilterType;
  value?: CalendarDate | number | string;
}

interface IJobsFilterControlState {
  tags: IJobsFilterTag[];
  filterDrawerValues: IJobsFilterControlModel;
  isViewTagsPopupShown: boolean;
  isFilterDrawerShown: boolean;
}

export interface IJobsFilterControlComposedProps {
  locale: string;
  productionTypes: IFacetGroup[];
  contractTypes: IFacetGroup[];
  placeholders: {
    productionType: string;
    contractType: string;
    dateSent: string;
    sender: string;
    playingAge: string;
    playingAgeFromLabel: string;
    playingAgeFromPlaceholder: string;
    playingAgeToLabel: string;
    playingAgeToPlaceholder: string;
    playingAgeEmptyLabel: string;
    yearLabel: string;
    yearsLabel: string;
    playingAgeOver: string;
    playingAgeUnder: string;
    locationLabel: string;
    searchLocationLabel: string;
    searchLocationPlaceholder: string;
  };
  changeFilters: ChangeFilters;
  filterValues: IFilterModel;
  filterConfig: IFilterConfig;
}

export interface IJobsFilterControlProps
  extends
    IJobsFilterControlComposedProps,
    LaunchDarklyClientWithFlags,
    WithTranslation {}

export class JobsFilterControl extends React.Component<
  IJobsFilterControlProps,
  IJobsFilterControlState
> {
  constructor(props: IJobsFilterControlProps) {
    super(props);

    const initialValues = {
      [FilterType.ProductionType]:
        props.filterConfig[FilterType.ProductionType].defaultValue,
      [FilterType.ContractType]:
        props.filterConfig[FilterType.ContractType].defaultValue,
      [FilterType.DateSent]:
        props.filterConfig[FilterType.DateSent].defaultValue,
      [FilterType.PlayingAgeMin]:
        props.filterConfig[FilterType.PlayingAgeMin].defaultValue,
      [FilterType.PlayingAgeMax]:
        props.filterConfig[FilterType.PlayingAgeMax].defaultValue,
      [FilterType.Locations]:
        props.filterConfig[FilterType.Locations].defaultValue,
    };
    this.state = {
      tags: [],
      filterDrawerValues: initialValues,
      isViewTagsPopupShown: false,
      isFilterDrawerShown: false,
    };
  }

  public componentDidMount() {
    this.refreshValuesAndTags();
  }

  public componentDidUpdate(prevProps: IJobsFilterControlProps) {
    if (!isEqual(this.props.filterConfig, prevProps.filterConfig)) {
      this.generateTags();
    }

    if (!isEqual(this.props.filterValues, prevProps.filterValues)) {
      this.refreshValuesAndTags();
    }
  }

  private updatePlayingAgeFilter = (values: IRangeSelectionValue) => {
    this.setState(
      {
        filterDrawerValues: {
          ...this.state.filterDrawerValues,
          [FilterType.PlayingAgeMin]: values.lowerRangeValue,
          [FilterType.PlayingAgeMax]: values.upperRangeValue,
        },
      },
      () => this.applyFilterDrawerFilters(false)
    );
  };

  public render() {
    const { locale, placeholders, filterValues, filterConfig } = this.props;
    const { tags, isFilterDrawerShown, filterDrawerValues } = this.state;
    const productionTypeConfig = this.getDropdownCheckboxGroupConfig(
      FilterType.ProductionType
    );
    const contractTypeConfig = this.getDropdownCheckboxGroupConfig(
      FilterType.ContractType
    );

    return (
      <div className="g-bg-secondary">
        <FilterControl
          onViewAllFilters={this.openFilterDrawer}
          onClearAllFilters={this.handleClearAllFilters}
          onApplyTags={this.handleApplyTags}
          locale={locale}
          tags={tags.map((tag) => tag.tagProps)}
          items={[
            <DropdownCheckbox
              key={FilterType.ProductionType}
              checkboxGroupConfig={productionTypeConfig}
              value={{
                ...mapOptionsToCheckboxGroupValue(
                  filterConfig[FilterType.ProductionType].checkboxOptions,
                  false
                ),
                ...filterDrawerValues[FilterType.ProductionType],
              }}
              valueChanged={this.bindFilterDrawerCheckboxGroupChangeHandler(
                FilterType.ProductionType,
                () =>
                  sendJobsfeedUsageHeapEventWithUserPrefix(
                    JobsFeedUsageEvents.FilterProductionType
                  )
              )}
              //@ts-expect-error figure out typing
              ValueComponent={DropdownCheckbox.getDefaultValueComponent({
                selectionPlaceholder: placeholders.productionType,
                selectionPrefix: "",
              })}
              onClose={() => this.applyFilterDrawerFilters(true)}
            />,
            <DropdownCheckbox
              key={FilterType.ContractType}
              checkboxGroupConfig={contractTypeConfig}
              value={{
                ...mapOptionsToCheckboxGroupValue(
                  filterConfig[FilterType.ContractType].checkboxOptions,
                  false
                ),
                ...filterDrawerValues[FilterType.ContractType],
              }}
              valueChanged={this.bindFilterDrawerCheckboxGroupChangeHandler(
                FilterType.ContractType,
                () =>
                  sendJobsfeedUsageHeapEventWithUserPrefix(
                    JobsFeedUsageEvents.FilterContract
                  )
              )}
              //@ts-expect-error figure out typing
              ValueComponent={DropdownCheckbox.getDefaultValueComponent({
                selectionPlaceholder: placeholders.contractType,
                selectionPrefix: "",
              })}
              onClose={() => this.applyFilterDrawerFilters(true)}
            />,
            <DatePickerUniversal
              key={FilterType.DateSent}
              type={DatePickerType.Range}
              allowTextInput={false}
              placeholder={placeholders.dateSent}
              errorMessageLocation={ErrorMessageLocation.Calendar}
              valueChanged={this.bindDateChangeHandler(
                FilterType.DateSent,
                () =>
                  sendJobsfeedUsageHeapEventWithUserPrefix(
                    JobsFeedUsageEvents.FilterDateSent
                  )
              )}
              value={filterValues[FilterType.DateSent]}
              muteExternalValueChange={true}
            />,
          ]}
        />
        {isFilterDrawerShown && (
          <FilterDrawer
            filters={this.prepareFilterDrawerFilters()}
            locale={locale}
            onClose={this.closeFilterDrawer}
            onClearAllFilters={this.handleClearAllFilters}
            onApplyFilters={() => this.applyFilterDrawerFilters(true)}
          />
        )}
      </div>
    );
  }

  private handleLocationFilterChange = (
    value: IOption<string> | IOption<string>[]
  ) => {
    this.setState(
      {
        filterDrawerValues: {
          ...this.state.filterDrawerValues,
          [FilterType.Locations]: value as IOption<string>[],
        },
      },
      () => this.applyFilterDrawerFilters(false)
    );
  };

  private prepareFilterDrawerFilters = () => {
    const { placeholders, filterConfig, flags, t } = this.props;
    const { filterDrawerValues } = this.state;
    const isYPPlayingAgeEnabled = flags[FEATURE_FLAGS.YPsPlayingAgeMin];

    const productionTypeFilterConfig = filterConfig[FilterType.ProductionType];
    const contractTypeFilterConfig = filterConfig[FilterType.ContractType];
    const playingAgeMax = filterDrawerValues[FilterType.PlayingAgeMax];
    const playingAgeMin = filterDrawerValues[FilterType.PlayingAgeMin];

    return [
      {
        title: placeholders.productionType,
        id: FilterType.ProductionType,
        selected: !isEmpty(
          mapCheckboxValueToList(filterDrawerValues[FilterType.ProductionType])
        ),
        renderFilterContent: () => (
          <CheckboxGroup
            config={{
              name: FilterType.ProductionType,
              options: productionTypeFilterConfig.checkboxOptions!,
            }}
            value={{
              ...mapOptionsToCheckboxGroupValue(
                productionTypeFilterConfig.checkboxOptions,
                false
              ),
              ...filterDrawerValues[FilterType.ProductionType],
            }}
            valueChanged={this.bindFilterDrawerCheckboxGroupChangeHandler(
              FilterType.ProductionType,
              () =>
                sendJobsfeedUsageHeapEventWithUserPrefix(
                  JobsFeedUsageEvents.FilterProductionType
                )
            )}
          />
        ),
      },
      {
        title: placeholders.contractType,
        id: FilterType.ContractType,
        selected: !isEmpty(
          mapCheckboxValueToList(filterDrawerValues[FilterType.ContractType])
        ),
        renderFilterContent: () => (
          <CheckboxGroup
            config={{
              name: FilterType.ContractType,
              options: contractTypeFilterConfig.checkboxOptions!,
            }}
            value={{
              ...mapOptionsToCheckboxGroupValue(
                contractTypeFilterConfig.checkboxOptions,
                false
              ),
              ...filterDrawerValues[FilterType.ContractType],
            }}
            valueChanged={this.bindFilterDrawerCheckboxGroupChangeHandler(
              FilterType.ContractType,
              () =>
                sendJobsfeedUsageHeapEventWithUserPrefix(
                  JobsFeedUsageEvents.FilterContract
                )
            )}
          />
        ),
      },
      {
        title: placeholders.dateSent,
        id: FilterType.DateSent,
        selected: Boolean(filterDrawerValues[FilterType.DateSent]),
        renderFilterContent: () => (
          <DatePickerUniversal
            key={FilterType.DateSent}
            type={DatePickerType.Range}
            allowTextInput={false}
            placeholder={placeholders.dateSent}
            errorMessageLocation={ErrorMessageLocation.Calendar}
            valueChanged={this.bindFilterDrawerDateChangeHandler(
              FilterType.DateSent,
              () =>
                sendJobsfeedUsageHeapEventWithUserPrefix(
                  JobsFeedUsageEvents.FilterDateSent
                )
            )}
            value={filterDrawerValues[FilterType.DateSent]}
          />
        ),
      },
      {
        title: t("common:label.playingAge"),
        id: "filter-playing-age",
        selected: _isNumber(playingAgeMin ?? playingAgeMax),
        renderFilterContent: () => (
          <RangeSelection
            value={{
              lowerRangeValue: playingAgeMin,
              upperRangeValue: playingAgeMax,
            }}
            getValueRepresentation={(value) => getPlayingAgeLabel(value, t)}
            minimumRangeValue={
              isYPPlayingAgeEnabled
                ? PlayingAge.YPPlayingAgeMinYrs
                : PlayingAge.PlayingAgeMinYrs
            }
            upperRangeMinOverride={isYPPlayingAgeEnabled && 1}
            maximumRangeValue={PlayingAge.PlayingAgeMaxYrs}
            valueChanged={this.updatePlayingAgeFilter}
            content={{
              lowerDropdownPlaceholder: t("common:label.select"),
              lowerDropdownLabel: t("common:label.from"),
              upperDropdownPlaceholder: t("common:label.select"),
              upperDropdownLabel: t("common:label.to"),
            }}
          />
        ),
      },
      {
        title: placeholders.locationLabel,
        id: FilterType.Locations,
        selected: !!filterDrawerValues[FilterType.Locations].length,
        renderFilterContent: () => {
          return (
            <LocationsSearch
              label={placeholders.searchLocationLabel}
              placeholder={placeholders.searchLocationPlaceholder}
              value={filterDrawerValues[FilterType.Locations]}
              onChange={this.handleLocationFilterChange}
              name={FilterType.Locations}
              isMultiple
            />
          );
        },
      },
    ];
  };

  private handleClearAllFilters = () => {
    const updatedValues = {} as IJobsFilterControlModel;

    Object.keys(this.props.filterConfig).forEach((filterId: FilterType) => {
      updatedValues[filterId] = this.props.filterConfig[filterId].defaultValue;
    });
    this.props.changeFilters(updatedValues);
  };

  private handleApplyTags = (tags: IFilterTagProps[]) => {
    const removedTags = this.state.tags.filter((tag) =>
      tags.every((t) => tag.tagProps.id !== t.id)
    );
    this.removeFilterTags(removedTags);
  };

  private bindFilterDrawerCheckboxGroupChangeHandler =
    (filterType: FilterType, callback?: () => void) =>
    (value: ICheckboxGroupValue) => {
      this.setState(
        {
          filterDrawerValues: {
            ...this.state.filterDrawerValues,
            [filterType]: value,
          },
        },
        () => this.applyFilterDrawerFilters(false)
      );

      callback?.();
    };

  private bindFilterDrawerDateChangeHandler =
    (filterType: FilterType, callback?: () => void) =>
    (date: Date[], detailsLevel: Detail, validity: DateValidity) => {
      const { filterDrawerValues } = this.state;

      if (
        validity === DateValidity.Valid &&
        (date === undefined || date.length > 1)
      ) {
        this.setState(
          {
            filterDrawerValues: { ...filterDrawerValues, [filterType]: date },
          },
          () => {
            this.applyFilterDrawerFilters(false);
          }
        );

        callback?.();
      }
    };

  private bindDateChangeHandler =
    (filterType: FilterType, callback?: () => void) =>
    (date: Date[], detailsLevel: Detail, validity: DateValidity) => {
      const { filterDrawerValues } = this.state;

      if (
        validity === DateValidity.Valid &&
        (date === undefined || date.length > 1)
      ) {
        const tags = this.getUpdatedTags(filterType, date);
        this.setState(
          {
            filterDrawerValues: { ...filterDrawerValues, [filterType]: date },
            tags,
          },
          () => {
            this.props.changeFilters({ [filterType]: date });
          }
        );

        callback?.();
      }
    };

  private getUpdatedTags(
    filterType: FilterType,
    value:
      | ValueOf<IFilterModel>
      | IOption<number>
      | (number | undefined)[]
      | IOption<string>[],
    useFilterDrawerControlType = false,
    initialTags = this.state.tags
  ): IJobsFilterTag[] {
    const { filterConfig, t } = this.props;

    if (!filterConfig[filterType]) {
      return initialTags;
    }

    let tags = initialTags.slice();
    const { filterControlType, drawerFilterControlType, facets, translations } =
      filterConfig[filterType];
    const controlType = useFilterDrawerControlType
      ? drawerFilterControlType
      : filterControlType;

    if (controlType === FilterControlType.Location) {
      const locationTags = (value as IOption<string>[])?.map((location) => {
        const tagId = location.value;

        const tag = {
          tagProps: {
            text: `${translations.locationLabel}: ${location.name}`,
            id: tagId,
            onClick: () => this.removeFilterTags([tag]),
          },
          value: location.value,
          filterType,
        };

        return tag;
      });

      tags = _uniqBy([...tags, ...locationTags], "tagProps.id");
    }

    if (filterType === FilterType.Sender) {
      const sender = value as IJobSender;
      const tagId = getTagId(filterType, filterType);
      tags = tags.filter((tag) => tag.tagProps.id !== tagId);

      if (sender?.id !== undefined) {
        tags.push(
          this.createJobsFilterTag(tagId, filterType, sender.name, sender.id)
        );
      }
    }

    if (controlType === FilterControlType.DatePicker) {
      const tagId = getTagId(filterType, filterType);
      tags = tags.filter((tag) => tag.tagProps.id !== tagId);

      if (value !== undefined) {
        const stringValue = DatePickerUniversal.getStringValue(
          value as CalendarDate
        );
        tags.push(
          this.createJobsFilterTag(
            tagId,
            filterType,
            stringValue,
            value as CalendarDate
          )
        );
      }
    }

    if (
      controlType === FilterControlType.CheckboxGroup &&
      value !== undefined
    ) {
      const selectedFacets = findFacets(
        facets,
        mapCheckboxGroupValueToNumericValues(value as ICheckboxGroupValue)
      );

      if (selectedFacets.length) {
        selectedFacets.forEach((facet) => {
          const tagId = getTagId(filterType, String(facet.id));
          tags = tags.filter((tag) => tag.tagProps.id !== tagId);

          if (!tags.find((tag) => tag.tagProps.id === tagId)) {
            tags.push(
              this.createJobsFilterTag(tagId, filterType, facet.name, facet.id)
            );
          }
        });
      }
    }

    if (controlType === FilterControlType.Dropdown) {
      const option = value as IOption<number>;
      const tagId = getTagId(filterType, String(option.value));

      if (value !== undefined) {
        if (!tags.find((tag) => tag.tagProps.id === tagId)) {
          tags.push(
            this.createJobsFilterTag(
              tagId,
              filterType,
              option.name,
              option.value
            )
          );
        }
      }
    }

    if (
      (filterType === FilterType.PlayingAgeMin ||
        filterType === FilterType.PlayingAgeMax) &&
      _isArray(value) &&
      _isNumber(value?.[0] ?? value?.[1])
    ) {
      const [min, max] = value;

      tags.push({
        filterType: FilterType.PlayingAgeMin,
        tagProps: {
          text: getPlayingAgePillTitle({
            t,
            lowerRangeValue: min as number,
            upperRangeValue: max as number,
          }),
          onClick: () =>
            this.setState((state) => {
              const filterDrawerValues = {
                ...state.filterDrawerValues,
                [FilterType.PlayingAgeMin]: undefined,
                [FilterType.PlayingAgeMax]: undefined,
              };

              this.props.changeFilters(filterDrawerValues);

              return {
                ...state,
                filterDrawerValues,
                tags: state.tags.filter(
                  (tag) => tag.filterType !== FilterType.PlayingAgeMin
                ),
              };
            }),
        },
      });
    }

    return tags;
  }

  private createJobsFilterTag(
    tagId: string,
    filterType: FilterType,
    name: string,
    value: CalendarDate | number | string
  ): IJobsFilterTag {
    const tag: IJobsFilterTag = {
      tagProps: {
        text: getTagName(
          this.props.filterConfig[filterType].translations,
          name
        ),
        id: tagId,
        onClick: () => this.removeFilterTags([tag]),
      },
      value,
      filterType,
    };

    return tag;
  }

  private refreshValuesAndTags() {
    this.setState({ filterDrawerValues: this.props.filterValues }, () =>
      this.generateTags()
    );
  }

  private generateTags = (updatedValues?: IFilterModel) => {
    const filterValues = updatedValues ?? this.props.filterValues;

    const updatedTags: IJobsFilterTag[] = Object.values(FilterType).reduce(
      (result, filterType) => {
        if (filterType === FilterType.PlayingAgeMin) {
          result = this.getUpdatedTags(
            filterType,
            [
              filterValues[FilterType.PlayingAgeMin],
              filterValues[FilterType.PlayingAgeMax],
            ],
            true,
            result
          );

          return result;
        }

        result = this.getUpdatedTags(
          filterType,
          filterValues[filterType],
          true,
          result
        );

        return result;
      },
      [] as IJobsFilterTag[]
    );

    this.setState({ tags: updatedTags });
  };

  private openFilterDrawer = () => {
    sendJobsfeedUsageHeapEventWithUserPrefix(
      JobsFeedUsageEvents.FilterViewAllFilters
    );
    this.setState({ isFilterDrawerShown: true });
  };

  private closeFilterDrawer = (updatedValues?: IFilterModel) => {
    this.setState({ isFilterDrawerShown: false }, () => {
      this.setState({
        filterDrawerValues: updatedValues ?? this.props.filterValues,
      });
    });
  };

  private applyFilterDrawerFilters = (closeDrawer: boolean) => {
    const { filterDrawerValues } = this.state;
    const { filterValues } = this.props;

    if (!isEqual(filterDrawerValues, filterValues)) {
      this.props.changeFilters(
        { ...filterDrawerValues, [FilterType.PageNumber]: 1 },
        (updatedValues: IFilterModel) => {
          this.generateTags(updatedValues);

          if (!closeDrawer) {
            this.setState({
              filterDrawerValues: updatedValues ?? this.props.filterValues,
            });
          } else {
            this.closeFilterDrawer(updatedValues);
          }
        }
      );
    } else {
      if (closeDrawer) {
        this.closeFilterDrawer();
      }
    }
  };

  private removeFilterTags = (tags: IJobsFilterTag[]) => {
    const updatedValues = transform(
      groupBy(tags, (tag) => tag.filterType),
      (model, tagsByType, filterType: FilterType) =>
        (model[filterType] = this.removeFilterTypeTags(filterType, tagsByType)),
      {
        ...this.props.filterValues,
        [FilterType.PageNumber]: 1,
      } as IJobsFilterControlModel
    );

    this.props.changeFilters(updatedValues);
  };

  private removeFilterTypeTags = (
    filterType: FilterType,
    filterTags: IJobsFilterTag[]
  ) => {
    const {
      filterControlType,
      drawerFilterControlType,
      checkboxOptions,
      defaultValue,
    } = this.props.filterConfig[filterType];

    if (drawerFilterControlType === FilterControlType.Location) {
      const removedValue = filterTags.map((tag) => tag.value);
      const locationValue = this.props.filterValues[
        filterType
      ] as IOption<string>[];

      return locationValue.filter(
        (option) => !removedValue.includes(option.value)
      );
    }

    if (drawerFilterControlType === FilterControlType.CheckboxGroup) {
      const checkboxGroupValue = this.props.filterValues[
        filterType
      ] as ICheckboxGroupValue;
      const filteredValues = mapCheckboxGroupValueToNumericValues(
        checkboxGroupValue
      ).filter((value) => filterTags.every((tag) => value !== tag.value));

      return mapNumericOptionsToCheckboxGroupValue(
        checkboxOptions,
        filteredValues
      );
    }

    if (
      filterControlType === FilterControlType.DatePicker ||
      drawerFilterControlType === FilterControlType.DatePicker
    ) {
      return defaultValue;
    }

    return undefined;
  };

  private getDropdownCheckboxGroupConfig(filterType: FilterType) {
    return {
      name: filterType,
      options: this.props.filterConfig[filterType].checkboxOptions,
    } as IDropdownCheckboxProps["checkboxGroupConfig"];
  }
}

export default compose(
  withTranslation(),
  withLaunchDarklyConsumerHOC()
)(JobsFilterControl);
